import {
  POOL_POCKETS,
  outcomesMatch,
  type PoolBall,
  type PoolPoint,
  type PoolShotOutcome,
} from '@awwyeah/shared';
import {
  POOL_FRAME_STRIDE,
  POOL_PHYSICS_DT,
  simulateShot,
  type PoolFrame,
  type PoolSoundEvent,
} from './physics';

/**
 * The render half of the fixed-timestep split (see `physics.ts`'s docblock) plus the
 * snapshot-trust guard. Two small jobs, kept out of the React component so both are
 * testable without a canvas:
 *
 *  1. `replayShot` — re-simulate a shot from the move log and reconcile the result against
 *     the shooter's recorded snapshot.
 *  2. `playShot` — walk a sample buffer in wall-clock time on `requestAnimationFrame`,
 *     interpolating between the two straddling 60 Hz samples so the motion is smooth on any
 *     display, at any refresh rate, without the physics ever seeing a real elapsed time.
 */

/** Wall-clock milliseconds between two recorded samples if played at true speed. */
export const POOL_FRAME_MS = POOL_PHYSICS_DT * POOL_FRAME_STRIDE * 1000;

/** No shot animation outruns this. A 12-second physics tail is *accurate* and *boring*;
 * past this the playback is time-compressed rather than made to wait. */
export const POOL_MAX_ANIMATION_MS = 8000;

/** A ball as the renderer wants it: `scale` shrinks a ball into a pocket on the sample
 * where it disappears, so a pot reads as a drop rather than a pop. */
export interface PoolRenderBall extends PoolBall {
  scale: number;
  /** `0`–`1` while the ball is dropping into a pocket — the renderer darkens it as it sinks. */
  sink?: number;
}

/** How long a pocketed ball takes to drop out of sight, wall-clock. */
export const POOL_DROP_MS = 320;

export interface PoolShotSimulation {
  /** What the app should treat as true — the recorded snapshot when there is one. */
  outcome: PoolShotOutcome;
  frames: PoolFrame[];
  /** Sound cues from our own simulation (display-only; never compared or stored). */
  events: PoolSoundEvent[];
  /** True when our own re-simulation disagreed with the recorded snapshot and we deferred
   * to the recording. Surfaced so the UI can note it rather than pretend nothing happened. */
  diverged: boolean;
}

/**
 * Re-simulates a shot from its move-log inputs, then reconciles with the shooter's
 * snapshot. **The snapshot always wins.** If our result differs we log a warning — loudly,
 * with the numbers — and use the recording anyway, because a family game where two phones
 * show different tables is broken in a way that "but our physics was right" doesn't fix.
 * The warning exists so we can find out whether cross-device float drift is real before we
 * decide to trust determinism alone.
 *
 * When there is no recorded snapshot (the shooter's own client, taking the shot for the
 * first time) our simulation *is* the truth and gets recorded.
 */
export function replayShot(
  startBalls: readonly PoolBall[],
  shot: { angle: number; power: number },
  recorded?: PoolShotOutcome,
): PoolShotSimulation {
  const sim = simulateShot(startBalls, shot);
  if (!recorded) {
    return { outcome: sim.outcome, frames: sim.frames, events: sim.events, diverged: false };
  }

  if (outcomesMatch(sim.outcome, recorded)) {
    return { outcome: recorded, frames: sim.frames, events: sim.events, diverged: false };
  }

  console.warn(
    '[pool] local simulation diverged from the recorded shot — trusting the recording.',
    { local: sim.outcome, recorded, shot },
  );
  // Play our frames (they're the only motion we have) but land on the recorded positions,
  // so the animation and the state it ends in agree.
  const frames = [...sim.frames, recorded.balls.map((b) => ({ ...b }))];
  return { outcome: recorded, frames, events: sim.events, diverged: true };
}

/** The pocket a disappearing ball went into — the nearest one to where it was last seen. */
export function nearestPocket(at: PoolPoint): PoolPoint {
  let best = POOL_POCKETS[0] as PoolPoint;
  let bestD = Number.POSITIVE_INFINITY;
  for (const pocket of POOL_POCKETS) {
    const d = Math.hypot(pocket.x - at.x, pocket.y - at.y);
    if (d < bestD) {
      bestD = d;
      best = pocket;
    }
  }
  return best;
}

/** Linear interpolation between two samples, matched by ball number. A ball present in `a`
 * but gone from `b` is the one that just dropped: it carries on into the mouth of its pocket
 * (then `playShot` sinks it), so a pot never vanishes on the open felt. */
export function interpolateFrames(a: PoolFrame, b: PoolFrame, t: number): PoolRenderBall[] {
  const next = new Map(b.map((ball) => [ball.n, ball]));
  const out: PoolRenderBall[] = [];
  for (const ball of a) {
    const to = next.get(ball.n);
    if (!to) {
      const pocket = nearestPocket(ball);
      out.push({
        n: ball.n,
        x: ball.x + (pocket.x - ball.x) * t,
        y: ball.y + (pocket.y - ball.y) * t,
        scale: 1,
      });
      continue;
    }
    out.push({
      n: ball.n,
      x: ball.x + (to.x - ball.x) * t,
      y: ball.y + (to.y - ball.y) * t,
      scale: 1,
    });
    next.delete(ball.n);
  }
  // A ball that appears in `b` only (the cue ball being placed) simply pops in.
  for (const ball of next.values()) out.push({ ...ball, scale: 1 });
  return out;
}

export interface PoolPlaybackHandle {
  /** Stops the animation immediately; safe to call twice. */
  cancel: () => void;
}

/** A pot, as the animation sees it: which ball, into which pocket, and when (sim ms). */
interface PoolDrop {
  n: number;
  pocket: PoolPoint;
  atMs: number;
}

/** Every pot in a sample buffer, from balls that are in one sample and gone from the next. */
export function dropsInFrames(frames: readonly PoolFrame[]): PoolDrop[] {
  const drops: PoolDrop[] = [];
  for (let i = 0; i + 1 < frames.length; i += 1) {
    const a = frames[i] as PoolFrame;
    const b = new Set((frames[i + 1] as PoolFrame).map((ball) => ball.n));
    for (const ball of a) {
      if (!b.has(ball.n))
        drops.push({ n: ball.n, pocket: nearestPocket(ball), atMs: (i + 1) * POOL_FRAME_MS });
    }
  }
  return drops;
}

export interface PoolPlaybackOptions {
  /** Sound cues to fire as the animation reaches them (sim ms, time-compressed with it). */
  events?: readonly PoolSoundEvent[];
  onEvent?: (event: PoolSoundEvent) => void;
}

/**
 * Plays `frames` in wall-clock time, calling `onFrame` once per `requestAnimationFrame`
 * with interpolated positions, then `onDone` once. Long shots are time-compressed to
 * `POOL_MAX_ANIMATION_MS` rather than truncated — you always see how the shot ended. A
 * pocketed ball rolls into its pocket and sinks over `POOL_DROP_MS` (in real time, not
 * compressed, so a long shot's pots still read as drops); sound cues fire as the playhead
 * passes them.
 */
export function playShot(
  frames: readonly PoolFrame[],
  onFrame: (balls: PoolRenderBall[]) => void,
  onDone: () => void,
  options: PoolPlaybackOptions = {},
): PoolPlaybackHandle {
  const spans = Math.max(1, frames.length - 1);
  const trueDuration = spans * POOL_FRAME_MS;
  const timeScale = Math.max(1, trueDuration / POOL_MAX_ANIMATION_MS);
  const drops = dropsInFrames(frames);
  const events = [...(options.events ?? [])].sort((l, r) => l.atMs - r.atMs);
  let nextEvent = 0;
  let raf = 0;
  let cancelled = false;
  const started = performance.now();

  const sinking = (wallElapsed: number): PoolRenderBall[] => {
    const out: PoolRenderBall[] = [];
    for (const drop of drops) {
      const k = (wallElapsed - drop.atMs / timeScale) / POOL_DROP_MS;
      if (k < 0 || k >= 1) continue;
      out.push({ n: drop.n, x: drop.pocket.x, y: drop.pocket.y, scale: 1 - 0.5 * k, sink: k });
    }
    return out;
  };

  const tick = (now: number): void => {
    if (cancelled) return;
    const wallElapsed = now - started;
    const simElapsed = wallElapsed * timeScale;
    while (nextEvent < events.length && (events[nextEvent] as PoolSoundEvent).atMs <= simElapsed) {
      options.onEvent?.(events[nextEvent] as PoolSoundEvent);
      nextEvent += 1;
    }
    const position = simElapsed / POOL_FRAME_MS;
    if (position >= spans) {
      const settled = (frames[spans] ?? []).map((b) => ({ ...b, scale: 1 }));
      const stillSinking = sinking(wallElapsed);
      onFrame([...stillSinking, ...settled]);
      if (stillSinking.length === 0) {
        onDone();
        return;
      }
      raf = requestAnimationFrame(tick);
      return;
    }
    const index = Math.floor(position);
    const a = frames[index];
    const b = frames[index + 1];
    if (a && b) onFrame([...sinking(wallElapsed), ...interpolateFrames(a, b, position - index)]);
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return {
    cancel: () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    },
  };
}
